import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { notification } from "../../utils/Notifications.tsx";
import {
  findRecordsByPartner,
  findRecordsForReport,
} from "../../services/assistanceCheck.service.ts";
import { formatDataForReport } from "../../utils/reporteChequeoUtils.ts";
import { ReporteChequeo, ReporteRow } from "../../models/ReporteChequeo.ts";
import { calcularTiempoTrabajado } from "../../interfaces/formik/IReporteRelojChecador.ts";
import * as moment from "moment-timezone";
import "moment/locale/es";
import {
  formatPayrollDisplayDate,
  formatPayrollDisplayDateShort,
} from "../../utils/payrollDateFormat.ts";
import { buildChartDetailedDisplayByFecha, recomputeChartDetailedHoursFromTimes } from "../../utils/chartDaysWorkedDisplay.ts";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import TableChecks from "./TableChecks.tsx";
import { isDateInRange } from "../../utils/index.ts";
import { DaysWorkedPayroll } from "../../models/DaysWorkedPayroll.ts";
import { IAuthorizedHours } from "../../models/AuthorizedHours.ts";
import {
  chartHorasExtrasExcludingDoubleShift,
  getPartnerHoursByDate,
} from "../../utils/payrollUtils.ts";
import {
  buildPayrollDisplayIncidentDateSet,
  filterIncidentsInPayrollDateRange,
} from "../../utils/incidentLeaderReviewFilter.ts";
import {
  adjustChartDetailedPayrollExtras,
  buildPayrollRhPendingFromSnapshot,
} from "../../utils/payrollRhPending.ts";
import type { PartnerIncidentCountSnapshot } from "../../services/incident.service.ts";
import {
  formatHoursToTime,
  formatPayrollHoursDecimal,
  formatPayrollHoursWithH,
} from "../../services/payroll/ui.ts";
import { findChartDaysWorked, findTotalIncidents } from "../../services/payroll.service.ts";
import { useFullDayHours } from "../../hooks/useFullDayHours.tsx";
import { usePayrollBranchMeta } from "../../hooks/usePayrollBranchMeta.ts";
import { resolvePayrollPartnerNightScheduleEnabled } from "../../utils/nightScheduleEligibility.ts";
import { getIncapacitiesByPartner } from "../../services/incapacities.service.ts";
import { getPermissionsByPartner } from "../../services/permissions.service.ts";
import { getScheduleByPartnerDateRange } from "../../services/branch-schedule.service.ts";
import {
  buildPartnerVisitHoursByFecha,
  mergeChartDetailedByDate,
  mapStoredVisit,
  enrichVisitsWithBranchNames,
} from "../../utils/payrollVisitHours.ts";
import { operationalDayKeyForVisitRow } from "../../utils/operationalDayReporteChequeo.ts";
import PayrollDayVisitRows from "../../components/nomina/PayrollDayVisitRows.tsx";
import {
  dedupePayrollVisitsByPair,
  isPlannedScheduleSlot,
  normalizeUnscheduledDetailedRow,
  resolveProgramadoDisplayForPayrollDay,
  shouldShowVariosRegistros,
  uniqueProgrammedSlotsFromSchedules,
  recomputeScheduleHoursForChartDay,
} from "../../utils/payrollVariosDisplay.ts";

const formatExtraHours = (extraHours: number): string => {
  if (extraHours <= 0) return "0";

  const integerHours = Math.floor(extraHours);
  const timeFormat = formatHoursToTime(extraHours);

  if (integerHours === 0) {
    return `0`;
  }

  return `${integerHours} (${timeFormat}h)`;
};

const PENDING_LEADER_CHART_TOOLTIP =
  "Incidencia o chequeo no programado pendiente de resolver en RH.";

const PAYROLL_UI = {
  ink: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  panel: "#ffffff",
  canvas: "#f8fafc",
  accent: "#50B7C0",
  accentDark: "#3a9ea6",
  accentGradient: "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)",
  navbarDark: "#212529",
  accentSoft: "#ecfeff",
  warn: "#f59e0b",
  warnSoft: "#fffbeb",
  danger: "#ef4444",
  shadow: "0 12px 40px rgba(15, 23, 42, 0.08)",
  shadowSm: "0 2px 8px rgba(15, 23, 42, 0.06)",
} as const;

/** Altura del modal (xxl) y padding interno del contenido. */
const CDW_LAYOUT = {
  modalFillHeight: "min(70vh, 560px)",
  contentPadding: "14px 16px 16px",
} as const;

const cdwFlexFill: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

function PayrollKpiCard({
  label,
  value,
  hint,
  accent,
  softBg,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
  softBg?: string;
}) {
  return (
    <div
      style={{
        flex: "1 1 96px",
        minWidth: 96,
        padding: "8px 10px",
        borderRadius: 8,
        border: `1px solid ${PAYROLL_UI.border}`,
        background: softBg || PAYROLL_UI.panel,
        boxShadow: PAYROLL_UI.shadowSm,
      }}
    >
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: PAYROLL_UI.muted,
          marginBottom: 2,
          lineHeight: 1.2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.05rem",
          fontWeight: 700,
          color: accent || PAYROLL_UI.ink,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {hint ? (
        <div
          style={{
            fontSize: "0.62rem",
            color: PAYROLL_UI.muted,
            marginTop: 2,
            lineHeight: 1.15,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function ChartDaysLoadingSkeleton() {
  return (
    <div style={{ padding: "8px 4px 20px" }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: "1 1 140px",
              height: 78,
              borderRadius: 12,
              background:
                "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
              backgroundSize: "200% 100%",
              animation: "cdwShimmer 1.2s ease-in-out infinite",
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: 380,
          borderRadius: 16,
          border: `1px solid ${PAYROLL_UI.border}`,
          background:
            "linear-gradient(90deg, #f8fafc 25%, #f1f5f9 50%, #f8fafc 75%)",
          backgroundSize: "200% 100%",
          animation: "cdwShimmer 1.2s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes cdwShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

function PayrollChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const horas = Number(payload[0]?.value ?? 0);
  return (
    <div
      style={{
        background: PAYROLL_UI.panel,
        border: `1px solid ${PAYROLL_UI.border}`,
        borderRadius: 10,
        padding: "12px 14px",
        boxShadow: PAYROLL_UI.shadow,
        minWidth: 168,
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: PAYROLL_UI.muted,
          marginBottom: 6,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {formatPayrollDisplayDate(String(label ?? ""))}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: PAYROLL_UI.accentDark,
        }}
      >
        {formatPayrollHoursWithH(horas)}
      </div>
      <div style={{ fontSize: "0.75rem", color: PAYROLL_UI.muted, marginTop: 4 }}>
        Horas registradas del día
      </div>
    </div>
  );
}

function PendingLeaderDotChart() {
  return (
    <span
      title={PENDING_LEADER_CHART_TOOLTIP}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "#f59e0b",
        marginLeft: 0,
        verticalAlign: "middle",
        cursor: "help",
      }}
      aria-label="Incidencia pendiente de revisión del líder"
    />
  );
}

interface DetailedWorkRecord {
  id: number;
  fecha: string;
  entradaProgramada: string;
  salidaProgramada: string;
  entradaRegistrada: string;
  salidaRegistrada: string;
  horasTrabajadasEnHorario: number;
  horasRegistradas: number;
  horasExtras: number;
  isFreeDayEnjoyed?: boolean;
  isAbsence?: boolean;
  isIncapacityOnly?: boolean;
  isPermissionOnly?: boolean;
  visitCount?: number;
  visits?: any[];
  salidaRegistradaFecha?: string;
}

function toScheduleDateKey(dateLike: string | Date | undefined | null): string {
  if (!dateLike) return "";
  if (typeof dateLike === "string" && moment(dateLike, "DD-MM-YYYY", true).isValid()) {
    return moment(dateLike, "DD-MM-YYYY").format("YYYY-MM-DD");
  }
  // exactDate / schedule_exactDate siempre en UTC civil (evita -1 día en MX).
  return moment.utc(dateLike).format("YYYY-MM-DD");
}

/** Horarios programados del día desde realSchedule y/o schedules de nómina (sin FKs). */
function normalizePayrollScheduleSlotSource(
  schedule: any
): { start?: string; end?: string; startTime?: string; endTime?: string; exactDate?: string } {
  if (!schedule) return {};
  const startTime =
    schedule.schedule_startTime || schedule.startTime || undefined;
  const endTime = schedule.schedule_endTime || schedule.endTime || undefined;
  // Preferir start/end de realSchedule (planilla) y también rangos schedule_* .
  return {
    start: schedule.start,
    end: schedule.end,
    startTime,
    endTime,
    exactDate: schedule.schedule_exactDate || schedule.exactDate,
  };
}

function dayScheduleSourcesForFecha(
  fecha: string,
  authorizedSchedule?: IAuthorizedHours[],
  payrollSchedules?: any[]
): Array<{ start?: string; end?: string; startTime?: string; endTime?: string }> {
  const ymd = toScheduleDateKey(fecha);

  const fromAuthorized = (authorizedSchedule || [])
    .filter((s) => toScheduleDateKey(s.exactDate) === ymd)
    .map((s) => normalizePayrollScheduleSlotSource(s));

  const fromPayroll = (payrollSchedules || [])
    .filter((s) => {
      const dateRaw = s.schedule_exactDate || s.exactDate;
      return dateRaw && toScheduleDateKey(dateRaw) === ymd;
    })
    .map((s) => normalizePayrollScheduleSlotSource(s));

  return [...fromAuthorized, ...fromPayroll];
}

/** Horarios programados del día (slots de planilla, no eco de checada). */
function getProgrammedSlotsForFecha(
  fecha: string,
  authorizedSchedule?: IAuthorizedHours[],
  payrollSchedules?: any[]
): { entrada: string; salida: string }[] {
  return uniqueProgrammedSlotsFromSchedules(
    dayScheduleSourcesForFecha(fecha, authorizedSchedule, payrollSchedules)
  ).filter((slot) => isPlannedScheduleSlot(slot.entrada, slot.salida));
}

const cdwVisitRowStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
};

function ChartDayProgramadoExpandRows({
  slots,
  fecha,
}: {
  slots: { entrada: string; salida: string }[];
  fecha: string;
}) {
  if (slots.length <= 1) return null;

  return (
    <>
      {slots.map((slot, idx) => (
        <tr
          key={`chart-schedule-${fecha}-${idx}`}
          className="payroll-day-visit-row"
          style={cdwVisitRowStyle}
        >
          <td
            style={{
              ...cdwCellStyle,
              fontSize: "0.72rem",
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            Horario {idx + 1}
          </td>
          <td style={{ ...cdwCellProgramadoStyle, ...cdwVisitRowStyle }}>
            {slot.entrada}
          </td>
          <td style={{ ...cdwCellProgramadoStyle, ...cdwVisitRowStyle }}>
            {slot.salida}
          </td>
          <td
            colSpan={5}
            style={{
              ...cdwCellStyle,
              ...cdwVisitRowStyle,
              color: "#94a3b8",
              fontSize: "0.72rem",
            }}
          >
            —
          </td>
        </tr>
      ))}
    </>
  );
}

const ChartDaysWorked = ({
  partner,
  dates,
  branch,
  authorizedSchedule,
  detailedRecords,
  realCheckRecords,
  extraIncidentDates,
  schedules,
  pendingLeaderReviewDates: pendingLeaderReviewDatesProp,
  incidentCountSnapshot,
  chartSavedIncidents,
  initialChartPayload,
}: {
  partner: number;
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  branch: number;
  authorizedSchedule: IAuthorizedHours[];
  detailedRecords: any[];
  realCheckRecords?: any[];
  extraIncidentDates?: string[];
  schedules?: any[];
  pendingLeaderReviewDates?: string[];
  incidentCountSnapshot?: PartnerIncidentCountSnapshot | null;
  chartSavedIncidents?: any[];
  initialChartPayload?: {
    graph?: any[];
    detailed?: any[];
    freeDaysDates?: string[];
    freeDaysEnjoyedDates?: string[];
    celebrityDaysDates?: string[];
    absenceRows?: any[];
    savedIncidents?: any[];
  };
}) => {
  const [view, setView] = React.useState<"graph" | "check">("graph");
  const [loading, setLoading] = React.useState(
    () => !Boolean(initialChartPayload?.detailed?.length)
  );
  const [data, setData] = React.useState<DaysWorkedPayroll[]>([]);
  const [detailedData, setDetailedData] = React.useState<DetailedWorkRecord[]>(
    []
  );
  const [freeDaysDates, setFreeDaysDates] = React.useState<Set<string>>(
    new Set()
  );
  const [celebrityDaysDates, setCelebrityDaysDates] = React.useState<
    Set<string>
  >(new Set());
  const [incidentDatesFromFetch, setIncidentDatesFromFetch] = React.useState<Set<string>>(new Set());
  const [pendingLeaderReviewDates, setPendingLeaderReviewDates] = React.useState<
    Set<string>
  >(() => new Set());
  const [pendingNoProgramadoDates, setPendingNoProgramadoDates] = React.useState<
    Set<string>
  >(() => new Set());
  const [pendingRhReviewDates, setPendingRhReviewDates] = React.useState<
    Set<string>
  >(() => new Set());
  const [incapacityDates, setIncapacityDates] = React.useState<string[]>([]);
  const [permissionDates, setPermissionDates] = React.useState<string[]>([]);
  /** BranchSchedule real del colaborador (horario planeado, sin FKs). */
  const [plannedBranchSchedules, setPlannedBranchSchedules] = React.useState<
    any[]
  >([]);
  const { fullDayHours: fullDayHoursFromHook } = useFullDayHours(branch);
  const payrollBranchMeta = usePayrollBranchMeta(realCheckRecords, branch);
  const partnerNightScheduleEnabled = React.useMemo(
    () =>
      resolvePayrollPartnerNightScheduleEnabled({
        partnerId: partner,
        branchId: branch,
        realCheckRecords: realCheckRecords as ReporteChequeo[] | undefined,
        payrollBranchMeta,
      }),
    [partner, branch, realCheckRecords, payrollBranchMeta]
  );
  const fullDayHours = React.useMemo(() => {
    const value = typeof fullDayHoursFromHook === 'number' && !isNaN(fullDayHoursFromHook) && fullDayHoursFromHook > 0
      ? fullDayHoursFromHook
      : 8.5;
    return value;
  }, [fullDayHoursFromHook]);

  React.useEffect(() => {
    if (pendingLeaderReviewDatesProp?.length) {
      setPendingLeaderReviewDates(new Set(pendingLeaderReviewDatesProp));
    }
  }, [pendingLeaderReviewDatesProp?.join("|")]);

  React.useEffect(() => {
    fetchChartData();
  }, [partner, branch, dates, initialChartPayload, incidentCountSnapshot]);

  const fetchChartData = async () => {
    const hasCachedChart = Boolean(initialChartPayload?.detailed?.length);
    if (!hasCachedChart) {
      setLoading(true);
    }
    try {
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");

      const response = hasCachedChart
        ? {
            graph: initialChartPayload?.graph || [],
            detailed: initialChartPayload?.detailed || [],
            freeDaysDates: initialChartPayload?.freeDaysDates || [],
            celebrityDaysDates: initialChartPayload?.celebrityDaysDates || [],
            absenceRows: initialChartPayload?.absenceRows || [],
          }
        : await findChartDaysWorked(
            partner,
            branch,
            startDate,
            endDate
          );

      const detailedDataMapped: DetailedWorkRecord[] = hasCachedChart
        ? ((initialChartPayload?.detailed || []) as DetailedWorkRecord[])
        : (response.detailed || []).map((item: any) => {
            const base: DetailedWorkRecord = {
              id: item.id,
              fecha: item.fecha,
              entradaProgramada: item.entradaProgramada,
              salidaProgramada: item.salidaProgramada,
              entradaRegistrada: item.entradaRegistrada,
              salidaRegistrada: item.salidaRegistrada,
              horasTrabajadasEnHorario: item.horasTrabajadasEnHorario || 0,
              horasRegistradas: item.horasRegistradas || 0,
              horasExtras: chartHorasExtrasExcludingDoubleShift(
                Number(item.horasRegistradas ?? 0),
                Number(item.horasExtras ?? 0),
                fullDayHours
              ),
              isFreeDayEnjoyed: item.isFreeDayEnjoyed || false,
              visitCount: item.visitCount,
              visits: item.visits,
              variosRegistros: item.variosRegistros,
            };
            return normalizeUnscheduledDetailedRow(base, [], {
              nightScheduleEnabled: partnerNightScheduleEnabled,
            });
          });

      const freeDaysDatesArray = response.freeDaysDates || [];
      setFreeDaysDates(new Set(freeDaysDatesArray));
      const celebrityDaysDatesArray = response.celebrityDaysDates || [];
      setCelebrityDaysDates(new Set(celebrityDaysDatesArray));

      const fetchedIncidentsRaw =
        initialChartPayload?.savedIncidents?.length
          ? initialChartPayload.savedIncidents
          : chartSavedIncidents?.length
            ? chartSavedIncidents
            : (await findTotalIncidents(partner, branch, startDate, endDate))
                .incidents || [];
      const visibleIncidents = filterIncidentsInPayrollDateRange(
        fetchedIncidentsRaw,
        startDate,
        endDate
      );
      setIncidentDatesFromFetch(
        buildPayrollDisplayIncidentDateSet(visibleIncidents)
      );

      const { rhPending, noProgramado } = buildPayrollRhPendingFromSnapshot(
        visibleIncidents,
        incidentCountSnapshot
      );
      if (pendingLeaderReviewDatesProp?.length) {
        for (const key of pendingLeaderReviewDatesProp) {
          rhPending.add(key);
        }
      }
      setPendingNoProgramadoDates(noProgramado);
      setPendingRhReviewDates(rhPending);
      setPendingLeaderReviewDates(rhPending);

      const [incapacityList, permissionList, branchScheduleRes] =
        await Promise.all([
          getIncapacitiesByPartner(partner),
          getPermissionsByPartner(partner).catch(() => []),
          getScheduleByPartnerDateRange(partner, startDate, endDate, 62).catch(
            () => ({ records: [] })
          ),
        ]);
      const plannedFromBranch = (branchScheduleRes?.records || []).map(
        (s: any) => ({
          exactDate: s.exactDate,
          startTime: s.startTime,
          endTime: s.endTime,
          schedule_startTime: s.startTime,
          schedule_endTime: s.endTime,
        })
      );
      setPlannedBranchSchedules(plannedFromBranch);
      const rangeStart = moment(startDate, "YYYY-MM-DD").startOf("day");
      const rangeEnd = moment(endDate, "YYYY-MM-DD").endOf("day");
      const incapacityDatesSet = new Set<string>();
      const permissionDatesSet = new Set<string>();

      (incapacityList || [])
        .filter((inc: any) => inc?.approved && !inc?.isRejected && inc?.startDate && inc?.endDate)
        .forEach((inc: any) => {
          const incStart = moment.unix(Number(inc.startDate)).startOf("day");
          const incEnd = moment.unix(Number(inc.endDate)).endOf("day");
          const overlapStart = moment.max(incStart, rangeStart.clone());
          const overlapEnd = moment.min(incEnd, rangeEnd.clone());
          if (overlapStart.isAfter(overlapEnd)) return;

          const cursor = overlapStart.clone().startOf("day");
          while (cursor.isSameOrBefore(overlapEnd, "day")) {
            incapacityDatesSet.add(cursor.format("DD-MM-YYYY"));
            cursor.add(1, "day");
          }
        });

      (permissionList || [])
        .filter(
          (permission: any) =>
            permission?.approved &&
            !permission?.isRejected &&
            permission?.startRequested &&
            permission?.endRequested
        )
        .forEach((permission: any) => {
          const permissionStart = moment.unix(Number(permission.startRequested)).startOf("day");
          const permissionEnd = moment.unix(Number(permission.endRequested)).endOf("day");
          const overlapStart = moment.max(permissionStart, rangeStart.clone());
          const overlapEnd = moment.min(permissionEnd, rangeEnd.clone());
          if (overlapStart.isAfter(overlapEnd)) return;

          const cursor = overlapStart.clone().startOf("day");
          while (cursor.isSameOrBefore(overlapEnd, "day")) {
            permissionDatesSet.add(cursor.format("DD-MM-YYYY"));
            cursor.add(1, "day");
          }
        });

      setIncapacityDates(
        Array.from(incapacityDatesSet).sort(
          (a, b) =>
            moment(a, "DD-MM-YYYY").valueOf() - moment(b, "DD-MM-YYYY").valueOf()
        )
      );
      setPermissionDates(
        Array.from(permissionDatesSet).sort(
          (a, b) =>
            moment(a, "DD-MM-YYYY").valueOf() - moment(b, "DD-MM-YYYY").valueOf()
        )
      );
      const adjustedDetailedData = adjustChartDetailedPayrollExtras(
        recomputeChartDetailedHoursFromTimes(detailedDataMapped, fullDayHours),
        visibleIncidents,
        incidentCountSnapshot
      );
      const absenceRows: DetailedWorkRecord[] = hasCachedChart
        ? []
        : (response.absenceRows || [])
            .filter(
              (row: any) =>
                !incapacityDatesSet.has(row.date) &&
                !permissionDatesSet.has(row.date)
            )
            .map((row: any) => ({
              id: 0,
              fecha: row.date,
              entradaProgramada: row.entradaProgramada || "N/A",
              salidaProgramada: row.salidaProgramada || "N/A",
              entradaRegistrada: "N/A",
              salidaRegistrada: "N/A",
              horasTrabajadasEnHorario: 0,
              horasRegistradas: 0,
              horasExtras: 0,
              isAbsence: true,
            }));

      const detailedWithAbsences = mergeChartDetailedByDate(
        [...adjustedDetailedData, ...absenceRows]
      ).sort(
        (a, b) =>
          moment(a.fecha, "DD-MM-YYYY").valueOf() -
          moment(b.fecha, "DD-MM-YYYY").valueOf()
      );
      detailedWithAbsences.forEach((item, index) => {
        item.id = index + 1;
      });
      setDetailedData(detailedWithAbsences);
      setData(
        detailedWithAbsences
          .filter((d) => !d.isAbsence && !d.isIncapacityOnly && !d.isPermissionOnly)
          .map((d) => ({
            horas: d.horasRegistradas,
            día: d.fecha,
            checkIn: d.entradaRegistrada,
            checkOut: d.salidaRegistrada,
          }))
      );
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setPendingLeaderReviewDates(new Set());
      setPendingNoProgramadoDates(new Set());
      setPendingRhReviewDates(new Set());
      notification(
        "Error ⚠️",
        "Error al obtener los datos del gráfico",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const transformData = () => {
    const final: DaysWorkedPayroll[] = [];
    const detailed: DetailedWorkRecord[] = [];

    const startDateUTC = moment
      .utc(dates.start.format("YYYY-MM-DD"))
      .startOf("day");
    const endDateUTC = moment.utc(dates.end.format("YYYY-MM-DD")).endOf("day");
    const startDateStr = startDateUTC.format("YYYY-MM-DD");
    const endDateStr = endDateUTC.format("YYYY-MM-DD");
    
    let filteredRealCheckRecords = realCheckRecords;
    if (realCheckRecords && realCheckRecords.length > 0) {
      const queryStartUTC = partnerNightScheduleEnabled
        ? moment.utc(startDateStr).subtract(1, "day").startOf("day")
        : startDateUTC;
      const queryEndUTC = partnerNightScheduleEnabled
        ? moment.utc(endDateStr).add(1, "day").endOf("day")
        : endDateUTC;
      filteredRealCheckRecords = realCheckRecords.filter((record) => {
        if (!record.assistanceCheck_checkDate) return false;
        const recordDateUTC = moment
          .utc(record.assistanceCheck_checkDate)
          .startOf("day");
        return (
          recordDateUTC.isSameOrAfter(queryStartUTC, "day") &&
          recordDateUTC.isSameOrBefore(queryEndUTC, "day")
        );
      });
    }

    if (filteredRealCheckRecords && filteredRealCheckRecords.length > 0) {
      const formattedData = formatDataForReport(
        filteredRealCheckRecords,
        startDateStr,
        endDateStr,
        payrollBranchMeta
      );

      const partnerData = formattedData.filter(
        (record) => record.checkIn?.partner_id === partner
      );

      for (const record of partnerData) {
        if (record.checkIn && record.checkOut) {
          const horasTrabajadas = calcularTiempoTrabajado(
            record.checkIn.assistanceCheck_checkTime,
            record.checkOut.assistanceCheck_checkTime
          );

          const visitDateKey = operationalDayKeyForVisitRow(
            record,
            payrollBranchMeta,
            filteredRealCheckRecords
          );
          final.push({
            horas: horasTrabajadas,
            día: moment.utc(visitDateKey).format("DD-MM-YYYY"),
            checkIn: record.checkIn.assistanceCheck_checkTime,
            checkOut: record.checkOut.assistanceCheck_checkTime,
          });
        }
      }
    } else if (detailedRecords && detailedRecords.length > 0) {
      const partnerRecords = getPartnerHoursByDate(
        detailedRecords,
        partner
      ).filter((record) => {
        const recordDateUTC = moment.utc(record.date).startOf("day");
        return (
          recordDateUTC.isSameOrAfter(startDateUTC, "day") &&
          recordDateUTC.isSameOrBefore(endDateUTC, "day")
        );
      });

      for (const record of partnerRecords) {
        final.push({
          horas: record.hoursWorked,
          día: moment.utc(record.date).format("DD-MM-YYYY"),
          checkIn: record.checkIn,
          checkOut: record.checkOut,
        });
      }
    } else {
      const filteredSchedule = authorizedSchedule.filter((data) => {
        const scheduleDateUTC = moment.utc(data.exactDate).startOf("day");
        return (
          scheduleDateUTC.isSameOrAfter(startDateUTC, "day") &&
          scheduleDateUTC.isSameOrBefore(endDateUTC, "day")
        );
      });

      for (const data of filteredSchedule) {
        const { worked, day, checkIn, checkOut } = data;

        const originalDate = moment(day).format("DD-MM-YYYY");

        final.push({
          horas: worked,
          día: originalDate,
          checkIn: checkIn.checkTime,
          checkOut: checkOut?.checkTime ?? "",
        });
      }
    }

    if (filteredRealCheckRecords && filteredRealCheckRecords.length > 0) {
      const formattedData = formatDataForReport(
        filteredRealCheckRecords,
        startDateStr,
        endDateStr,
        payrollBranchMeta
      );

      const partnerData = formattedData.filter(
        (record) => record.checkIn?.partner_id === partner
      );

      for (const record of partnerData) {
        if (record.checkIn && record.checkOut) {
          const schedule = authorizedSchedule.find((s) => {
              const scheduleDay = moment(s.exactDate).format("YYYY-MM-DD");
            const checkDay = moment
              .utc(record.checkIn!.assistanceCheck_checkDate)
              .format("YYYY-MM-DD");
              return scheduleDay === checkDay;
          });

          const entradaRegistrada = record.checkIn!.assistanceCheck_checkTime;
          const salidaRegistrada = record.checkOut!.assistanceCheck_checkTime;
          const entradaProgramada = schedule
            ? schedule.start + ":00"
            : entradaRegistrada;
          const salidaProgramada = schedule
            ? schedule.end + ":00"
            : salidaRegistrada;

          const horasRegistradas = calcularTiempoTrabajado(
            entradaRegistrada,
            salidaRegistrada
          );

          let horasTrabajadasEnHorario = 0;
          let horasExtras = 0;

          if (schedule) {
            const recomputed = recomputeScheduleHoursForChartDay({
              entradaRegistrada,
              salidaRegistrada,
              entradaProgramada,
              salidaProgramada,
              horasRegistradas,
              fullDayHours,
            });
            horasTrabajadasEnHorario = recomputed.horasTrabajadasEnHorario;
            let diferencia = recomputed.horasExtrasRaw;

            if (schedules && schedules.length > 0) {
              const scheduleDate = moment
                .utc(record.checkIn.assistanceCheck_checkDate)
                .format("YYYY-MM-DD");
              const scheduleWithIncidents = schedules.find(
                (s) =>
                  moment(s.schedule_exactDate).format("YYYY-MM-DD") ===
                  scheduleDate
              );

              if (scheduleWithIncidents && scheduleWithIncidents.incidents) {
                const hasRejectedLateCheckout =
                  scheduleWithIncidents.incidents.some(
                    (incident) =>
                      incident.status === "Salió tarde" &&
                      incident.finalStatus === false
                  );

                const hasRejectedEarlyCheckin =
                  scheduleWithIncidents.incidents.some(
                    (incident) =>
                      incident.status === "Llegó temprano" &&
                      incident.finalStatus === false
                  );

                if (hasRejectedLateCheckout || hasRejectedEarlyCheckin) {
                  diferencia = 0;
                }
              }
            }

            horasExtras = diferencia > 0 ? diferencia : 0;
            horasExtras = chartHorasExtrasExcludingDoubleShift(
              horasRegistradas,
              horasExtras,
              fullDayHours
            );
          } else {
            horasTrabajadasEnHorario = horasRegistradas;
            horasExtras = 0;
          }

          detailed.push({
            id: detailed.length + 1,
            fecha: moment
              .utc(record.checkIn.assistanceCheck_checkDate)
              .format("DD-MM-YYYY"),
            entradaProgramada: entradaProgramada,
            salidaProgramada: salidaProgramada,
            entradaRegistrada: entradaRegistrada,
            salidaRegistrada: salidaRegistrada,
            horasTrabajadasEnHorario: horasTrabajadasEnHorario,
            horasRegistradas: horasRegistradas,
            horasExtras: horasExtras,
          });
        }
      }
    } else if (detailedRecords && detailedRecords.length > 0) {
      const partnerRecords = getPartnerHoursByDate(
        detailedRecords,
        partner
      ).filter((record) => {
        const recordDateUTC = moment.utc(record.date).startOf("day");
        return (
          recordDateUTC.isSameOrAfter(startDateUTC, "day") &&
          recordDateUTC.isSameOrBefore(endDateUTC, "day")
        );
      });

      for (const record of partnerRecords) {
        const schedule = authorizedSchedule.find((s) => {
            const scheduleDay = moment(s.exactDate).format("YYYY-MM-DD");
            return scheduleDay === record.date;
        });

        if (schedule) {
          const entradaProgramada = schedule.start + ":00";
          const salidaProgramada = schedule.end + ":00";
          const entradaRegistrada = record.checkIn;
          const salidaRegistrada = record.checkOut;

          const totalHoursWorked = record.hoursWorked;
          const recomputed = recomputeScheduleHoursForChartDay({
            entradaRegistrada,
            salidaRegistrada,
            entradaProgramada,
            salidaProgramada,
            horasRegistradas: totalHoursWorked,
            fullDayHours,
          });
          let horasTrabajadasEnHorario = recomputed.horasTrabajadasEnHorario;
          let diferencia = recomputed.horasExtrasRaw;

          if (schedules && schedules.length > 0) {
            const scheduleDate = record.date;
            const scheduleWithIncidents = schedules.find(
              (s) =>
                moment(s.schedule_exactDate).format("YYYY-MM-DD") ===
                scheduleDate
            );

            if (scheduleWithIncidents && scheduleWithIncidents.incidents) {
              const hasRejectedLateCheckout =
                scheduleWithIncidents.incidents.some(
                  (incident) =>
                    incident.status === "Salió tarde" &&
                    incident.finalStatus === false
                );

              const hasRejectedEarlyCheckin =
                scheduleWithIncidents.incidents.some(
                  (incident) =>
                    incident.status === "Llegó temprano" &&
                    incident.finalStatus === false
                );

              if (hasRejectedLateCheckout || hasRejectedEarlyCheckin) {
                diferencia = 0;
              }
            }
          }

          let horasExtras = diferencia > 0 ? diferencia : 0;
          horasExtras = chartHorasExtrasExcludingDoubleShift(
            totalHoursWorked,
            horasExtras,
            fullDayHours
          );

          detailed.push({
            id: detailed.length + 1,
            fecha: moment.utc(record.date).format("DD-MM-YYYY"),
            entradaProgramada: entradaProgramada,
            salidaProgramada: salidaProgramada,
            entradaRegistrada: entradaRegistrada,
            salidaRegistrada: salidaRegistrada,
            horasTrabajadasEnHorario: horasTrabajadasEnHorario,
            horasRegistradas: record.hoursWorked,
            horasExtras: horasExtras,
          });
        }
      }
    }

    final.sort(
      (a, b) =>
        moment.utc(a.día, "DD-MM-YYYY").valueOf() -
        moment.utc(b.día, "DD-MM-YYYY").valueOf()
    );
    detailed.sort(
      (a, b) =>
        moment.utc(a.fecha, "DD-MM-YYYY").valueOf() -
        moment.utc(b.fecha, "DD-MM-YYYY").valueOf()
    );

    const finalFiltered = final.filter((item) => {
      const itemDate = moment.utc(item.día, "DD-MM-YYYY").startOf("day");
      return (
        itemDate.isSameOrAfter(startDateUTC, "day") &&
        itemDate.isSameOrBefore(endDateUTC, "day")
      );
    });

    const detailedFiltered = detailed.filter((item) => {
      const itemDate = moment.utc(item.fecha, "DD-MM-YYYY").startOf("day");
      return (
        itemDate.isSameOrAfter(startDateUTC, "day") &&
        itemDate.isSameOrBefore(endDateUTC, "day")
      );
    });

    try {
      const requestedStart = dates.start?.format("DD-MM-YYYY");
      const requestedEnd = dates.end?.format("DD-MM-YYYY");
      const fetchedDates = finalFiltered.map((r) => r.día);
      console.log(`SOLICITADA: ${requestedStart} al ${requestedEnd}`);
      console.log(`FETCH: [${fetchedDates.join(", ")}]`);
    } catch {}

    const detailedMerged = mergeChartDetailedByDate(detailedFiltered).map(
      (item, index) => {
        const daySchedules = dayScheduleSourcesForFecha(
          item.fecha,
          authorizedSchedule,
          [...(schedules || []), ...plannedBranchSchedules]
        );
        const dayVisits = (item.visits || []).map((v: any) =>
          mapStoredVisit(v, item.fecha)
        );
        const programadoSlots = uniqueProgrammedSlotsFromSchedules(
          daySchedules
        ).filter((slot) => isPlannedScheduleSlot(slot.entrada, slot.salida));
        const programado =
          programadoSlots.length > 1
            ? { entrada: "Varios", salida: "Varios" }
            : programadoSlots.length === 1
              ? programadoSlots[0]
              : resolveProgramadoDisplayForPayrollDay(
                  daySchedules,
                  dedupePayrollVisitsByPair(dayVisits),
                  { nightScheduleEnabled: partnerNightScheduleEnabled }
                );
        const mergedRow = {
          ...item,
          id: index + 1,
          entradaProgramada:
            programadoSlots.length === 0 &&
            item.entradaRegistrada &&
            item.entradaRegistrada !== "N/A"
              ? item.entradaRegistrada
              : programado.entrada,
          salidaProgramada:
            programadoSlots.length === 0 &&
            item.salidaRegistrada &&
            item.salidaRegistrada !== "N/A"
              ? item.salidaRegistrada
              : programado.salida,
        };
        return normalizeUnscheduledDetailedRow(mergedRow, daySchedules, {
          nightScheduleEnabled: partnerNightScheduleEnabled,
        });
      }
    );

    setData(finalFiltered);
    setDetailedData(detailedMerged);
    setLoading(false);
  };

  const payrollKpis = React.useMemo(() => {
    const displayMap = buildChartDetailedDisplayByFecha(detailedData, {
      branchId: branch,
      authorizedSchedule,
      payrollSchedules: [...(schedules || []), ...plannedBranchSchedules],
      realCheckRecords,
      partnerId: partner,
      periodStart: dates.start.format("YYYY-MM-DD"),
      periodEnd: dates.end.format("YYYY-MM-DD"),
      payrollBranchMeta,
      fullDayHours,
    });
    const displayed = detailedData.map(
      (row) => displayMap.get(row.fecha) ?? row
    );
    const totalHoras = displayed.reduce(
      (sum, row) =>
        sum + (row.isAbsence ? 0 : Number(row.horasRegistradas || 0)),
      0
    );
    const diasConRegistro = displayed.filter(
      (row) => !row.isAbsence && Number(row.horasRegistradas || 0) > 0
    ).length;
    const horasExtras = displayed.reduce(
      (sum, row) => sum + Number(row.horasExtras || 0),
      0
    );
    const horasEnHorario = displayed.reduce(
      (sum, row) => sum + Number(row.horasTrabajadasEnHorario || 0),
      0
    );
    const faltas = detailedData.filter((row) => row.isAbsence === true).length;
    const maxHoras = Math.max(
      fullDayHours,
      ...displayed.map((row) => Number(row.horasRegistradas || 0)),
      1
    );
    return {
      totalHoras,
      diasConRegistro,
      horasExtras,
      horasEnHorario,
      faltas,
      maxHoras,
      yMax: Math.ceil(Math.max(maxHoras * 1.12, fullDayHours + 1)),
    };
  }, [
    detailedData,
    fullDayHours,
    branch,
    authorizedSchedule,
    schedules,
    plannedBranchSchedules,
    realCheckRecords,
    partner,
    dates,
    payrollBranchMeta,
  ]);

  const getBarFill = (entry: { horas?: number }) => {
    if (Number(entry.horas) > fullDayHours + 0.25) {
      return "url(#cdwBarExtra)";
    }
    return "url(#cdwBarNormal)";
  };

  return (
    <React.Fragment>
      <div
        style={{
          margin: "-1.5rem",
          minHeight: CDW_LAYOUT.modalFillHeight,
          height: CDW_LAYOUT.modalFillHeight,
          display: "flex",
          flexDirection: "column",
          background: PAYROLL_UI.canvas,
          borderRadius: 0,
          border: "none",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 18px 12px",
            background: "transparent",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            {partnerNightScheduleEnabled ? (
              <div
                style={{
                  fontSize: "0.65rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: PAYROLL_UI.accentSoft,
                  border: `1px solid ${PAYROLL_UI.accent}`,
                  color: PAYROLL_UI.accentDark,
                  marginRight: "auto",
                }}
              >
                <i className="fas fa-moon" style={{ fontSize: "0.65rem" }} />
                Turno nocturno (sucursal y colaborador)
              </div>
            ) : null}

            {!loading ? (
              <div
                style={{
                  display: "inline-flex",
                  padding: 3,
                  borderRadius: 8,
                  background: PAYROLL_UI.canvas,
                  border: `1px solid ${PAYROLL_UI.border}`,
                  gap: 3,
                }}
              >
                {(
                  [
                    { id: "graph" as const, label: "Gráfica", icon: "fa-chart-column" },
                    { id: "check" as const, label: "Registros", icon: "fa-table-list" },
                  ] as const
                ).map((tab) => {
                  const active = view === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className="border-0"
                      style={{
                        padding: "5px 12px",
                        borderRadius: 7,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        color: active ? "#fff" : PAYROLL_UI.muted,
                        background: active
                          ? PAYROLL_UI.accentGradient
                          : "transparent",
                        boxShadow: active
                          ? "0 2px 8px rgba(80, 183, 192, 0.25)"
                          : "none",
                        transition: "background 0.15s ease, color 0.15s ease",
                      }}
                      onClick={() =>
                        React.startTransition(() => setView(tab.id))
                      }
                    >
                      <i
                        className={`fas ${tab.icon}`}
                        style={{ marginRight: 6, fontSize: "0.75rem" }}
                      />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div
          style={{
            ...cdwFlexFill,
            padding: CDW_LAYOUT.contentPadding,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <ChartDaysLoadingSkeleton />
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 10,
                  flexShrink: 0,
                }}
              >
                <PayrollKpiCard
                  label="Días con registro"
                  value={String(payrollKpis.diasConRegistro)}
                  hint={`de ${data.length} en el periodo`}
                />
                <PayrollKpiCard
                  label="Faltas"
                  value={String(payrollKpis.faltas)}
                  hint={
                    payrollKpis.faltas > 0
                      ? "Programado sin chequeo"
                      : "Sin faltas en el periodo"
                  }
                  accent="#b91c1c"
                  softBg="#fef2f2"
                />
                <PayrollKpiCard
                  label="Horas registradas"
                  value={formatPayrollHoursWithH(payrollKpis.totalHoras)}
                  hint="Suma del periodo"
                  accent={PAYROLL_UI.accentDark}
                  softBg={PAYROLL_UI.accentSoft}
                />
                <PayrollKpiCard
                  label="Horas en horario"
                  value={formatPayrollHoursDecimal(payrollKpis.horasEnHorario)}
                  hint="Según programación"
                />
                <PayrollKpiCard
                  label="Horas extras"
                  value={formatPayrollHoursDecimal(payrollKpis.horasExtras)}
                  hint="Después de incidencias"
                  accent={
                    payrollKpis.horasExtras > 0 ? PAYROLL_UI.accentDark : PAYROLL_UI.ink
                  }
                />
              </div>

              <div style={cdwFlexFill}>
      {view === "graph" ? (
        <div
          style={{
            ...cdwFlexFill,
            borderRadius: 10,
            padding: "12px 14px 10px",
            backgroundColor: PAYROLL_UI.panel,
            border: `1px solid ${PAYROLL_UI.border}`,
            boxShadow: PAYROLL_UI.shadowSm,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 6,
              marginBottom: 6,
              padding: "0 2px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  color: PAYROLL_UI.ink,
                }}
              >
                Horas por día
              </div>
              <div style={{ fontSize: "0.65rem", color: PAYROLL_UI.muted }}>
                Línea roja = jornada ({formatPayrollHoursWithH(fullDayHours)})
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                fontSize: "0.65rem",
                color: PAYROLL_UI.muted,
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: "linear-gradient(180deg, #5eead4, #0d9488)",
                  }}
                />
                Normal
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: "linear-gradient(180deg, #62C9D1, #50B7C0)",
                  }}
                />
                Sobre jornada
              </span>
            </div>
          </div>

          <div style={{ ...cdwFlexFill, minHeight: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 8, right: 16, left: 4, bottom: 4 }}
              barCategoryGap="8%"
              barSize={Math.min(56, Math.max(32, 720 / Math.max(data.length, 1)))}
            >
              <defs>
                <linearGradient id="cdwBarNormal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5eead4" stopOpacity={1} />
                  <stop offset="100%" stopColor="#0d9488" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="cdwBarExtra" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#62C9D1" stopOpacity={1} />
                  <stop offset="100%" stopColor="#50B7C0" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#e2e8f0"
                vertical={false}
              />
              <XAxis
                dataKey="día"
                tickLine={false}
                axisLine={{ stroke: PAYROLL_UI.border, strokeWidth: 1 }}
                interval={data.length > 18 ? 1 : 0}
                angle={data.length > 12 ? -40 : 0}
                textAnchor={data.length > 12 ? "end" : "middle"}
                height={data.length > 12 ? 52 : 28}
                tick={{ fill: PAYROLL_UI.muted, fontSize: 10 }}
                tickFormatter={(value) =>
                  formatPayrollDisplayDate(String(value))
                }
              />
              <YAxis
                domain={[0, payrollKpis.yMax]}
                tickLine={false}
                axisLine={false}
                tick={{ fill: PAYROLL_UI.muted, fontSize: 10 }}
                width={32}
                tickFormatter={(v) => `${v}h`}
              />
              <Tooltip
                content={<PayrollChartTooltip />}
                cursor={{ fill: "rgba(80, 183, 192, 0.08)" }}
              />
              <Bar
                dataKey="horas"
                radius={[6, 6, 0, 0]}
                name="Horas trabajadas"
                maxBarSize={64}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarFill(entry)} />
                ))}
              </Bar>
              <ReferenceLine
                y={fullDayHours}
                stroke={PAYROLL_UI.danger}
                strokeWidth={2}
                strokeDasharray="6 4"
                label={{
                  value: `Jornada ${formatPayrollHoursWithH(fullDayHours)}`,
                  position: "insideTopRight",
                  fill: PAYROLL_UI.danger,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
          </div>
        </div>
      ) : null}
      {view === "check" ? (
        <div
          style={{
            ...cdwFlexFill,
            borderRadius: 10,
            border: `1px solid ${PAYROLL_UI.border}`,
            background: PAYROLL_UI.panel,
            overflow: "hidden",
            boxShadow: PAYROLL_UI.shadowSm,
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderBottom: `1px solid ${PAYROLL_UI.border}`,
              background:
                "linear-gradient(90deg, #f8fafc 0%, #ecfeff 100%)",
            }}
          >
            <div
              style={{
                fontSize: "0.82rem",
                fontWeight: 700,
                color: PAYROLL_UI.ink,
              }}
            >
              Detalle diario de asistencia
            </div>
            <div style={{ fontSize: "0.65rem", color: PAYROLL_UI.muted, marginTop: 1 }}>
              Horas registradas
            </div>
          </div>
          <div
            style={{
              ...cdwFlexFill,
              padding: "10px 12px 12px",
              overflow: "hidden",
            }}
          >
          {detailedData.length > 0 ? (
            <DetailedWorkTable
              records={detailedData}
              partnerId={partner}
              authorizedSchedule={authorizedSchedule}
              payrollSchedules={[
                ...(schedules || []),
                ...plannedBranchSchedules,
              ]}
              realCheckRecords={realCheckRecords}
              periodStart={dates.start.format("YYYY-MM-DD")}
              periodEnd={dates.end.format("YYYY-MM-DD")}
              payrollBranchMeta={payrollBranchMeta}
              branchId={branch}
              fullDayHours={fullDayHours}
              extraIncidentDates={extraIncidentDates}
              freeDaysDates={freeDaysDates}
              celebrityDaysDates={celebrityDaysDates}
              fetchedIncidentDates={incidentDatesFromFetch}
              pendingLeaderReviewDates={pendingRhReviewDates}
              pendingNoProgramadoDates={pendingNoProgramadoDates}
              incapacityDates={incapacityDates}
              permissionDates={permissionDates}
            />
          ) : (
            <TableChecks records={data} />
          )}
          </div>
        </div>
      ) : null}
              </div>
            </>
          )}
        </div>
      </div>

    </React.Fragment>
  );
};

/** Contenedor de tabla de registros (cuerpo sigue con columnas programado/real). */
const cdwTableWrapStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  borderRadius: 8,
  border: "none",
  overflow: "auto",
  background: "#ffffff",
};

const cdwTableStyle: React.CSSProperties = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
  fontSize: "0.74rem",
  marginBottom: 0,
};

/** Encabezado tabla nómina (color sólido + gradiente; sticky requiere backgroundColor). */
const cdwHeaderThStyle: React.CSSProperties = {
  backgroundColor: "#ecfeff",
  backgroundImage: "linear-gradient(180deg, #f8fafc 0%, #ecfeff 100%)",
  color: "#0f766e",
  fontWeight: 700,
  fontSize: "0.62rem",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  textAlign: "center",
  padding: "0.38rem 0.3rem",
  borderBottom: "2px solid #99f6e4",
  borderRight: "none",
  borderLeft: "none",
  whiteSpace: "normal",
  verticalAlign: "middle",
  lineHeight: 1.25,
};

const cdwCellStyle: React.CSSProperties = {
  padding: "0.28rem 0.35rem",
  verticalAlign: "middle",
  borderBottom: "1px solid #e2e8f0",
  borderRight: "none",
  borderLeft: "none",
  fontSize: "0.72rem",
  color: "#334155",
  textAlign: "center",
};

const cdwCellProgramadoStyle: React.CSSProperties = {
  ...cdwCellStyle,
  backgroundColor: "#ecfdf5",
};

const cdwCellRealStyle: React.CSSProperties = {
  ...cdwCellStyle,
  backgroundColor: "#fffbeb",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
  textAlign: "center",
  verticalAlign: "middle",
};

const cdwRealTimeStackStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  minWidth: 0,
  maxWidth: "100%",
  margin: "0 auto",
};

const cdwRealTimeLineStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  lineHeight: 1.2,
  fontVariantNumeric: "tabular-nums",
};

const cdwRealDateLineStyle: React.CSSProperties = {
  fontSize: "0.62rem",
  color: "#94a3b8",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  lineHeight: 1.15,
};

const ChartRealTimeCell = ({
  time,
  crossDayDate,
}: {
  time?: string;
  crossDayDate?: string;
}) => (
  <div style={cdwRealTimeStackStyle}>
    <span style={cdwRealTimeLineStyle}>{time}</span>
    {crossDayDate ? (
      <span style={cdwRealDateLineStyle}>
        {formatPayrollDisplayDateShort(crossDayDate)}
      </span>
    ) : null}
  </div>
);

const cdwCellWrapStyle: React.CSSProperties = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.35,
  textAlign: "center",
};

const cdwFooterCellStyle: React.CSSProperties = {
  ...cdwCellStyle,
  fontWeight: 700,
  background: "#ecfeff",
  borderTop: "2px solid #bae6fd",
};

const DetailedWorkTable = ({
  records,
  partnerId,
  authorizedSchedule,
  payrollSchedules,
  realCheckRecords,
  periodStart,
  periodEnd,
  payrollBranchMeta,
  branchId,
  fullDayHours = 8.5,
  extraIncidentDates,
  freeDaysDates,
  celebrityDaysDates,
  fetchedIncidentDates,
  pendingLeaderReviewDates,
  pendingNoProgramadoDates,
  incapacityDates,
  permissionDates,
}: {
  partnerId: number;
  authorizedSchedule?: IAuthorizedHours[];
  payrollSchedules?: any[];
  realCheckRecords?: any[];
  periodStart: string;
  periodEnd: string;
  payrollBranchMeta?: Record<
    number,
    import("../../utils/operationalDayReporteChequeo.ts").BranchReportMeta
  >;
  branchId?: number;
  fullDayHours?: number;
  records: DetailedWorkRecord[];
  extraIncidentDates?: string[];
  freeDaysDates?: Set<string>;
  celebrityDaysDates?: Set<string>;
  fetchedIncidentDates?: Set<string>;
  pendingLeaderReviewDates?: Set<string>;
  pendingNoProgramadoDates?: Set<string>;
  incapacityDates?: string[];
  permissionDates?: string[];
}) => {
  const datesWithIncidents = React.useMemo(
    () =>
      new Set([
        ...(extraIncidentDates || []),
        ...Array.from(fetchedIncidentDates || new Set()),
      ]),
    [extraIncidentDates, fetchedIncidentDates]
  );
  const existingRecordDatesSet = React.useMemo(
    () => new Set((records || []).map((record) => record.fecha)),
    [records]
  );
  const incapacityDatesSet = React.useMemo(
    () => new Set(incapacityDates || []),
    [incapacityDates]
  );
  const permissionDatesSet = React.useMemo(
    () => new Set(permissionDates || []),
    [permissionDates]
  );
  const extraStatusDates = React.useMemo(
    () =>
      Array.from(new Set([...(incapacityDates || []), ...(permissionDates || [])]))
        .filter((date) => !existingRecordDatesSet.has(date)),
    [incapacityDates, permissionDates, existingRecordDatesSet]
  );
  const tableRows = React.useMemo(() => {
    const rows: DetailedWorkRecord[] = [
      ...(records || []),
      ...extraStatusDates.map((date) => ({
        id: 0,
        fecha: date,
        entradaProgramada: "N/A",
        salidaProgramada: "N/A",
        entradaRegistrada: "N/A",
        salidaRegistrada: "N/A",
        horasTrabajadasEnHorario: 0,
        horasRegistradas: 0,
        horasExtras: 0,
        isIncapacityOnly: incapacityDatesSet.has(date),
        isPermissionOnly: permissionDatesSet.has(date),
      })),
    ];

    return rows.sort(
      (a, b) =>
        moment(a.fecha, "DD-MM-YYYY").valueOf() -
        moment(b.fecha, "DD-MM-YYYY").valueOf()
    );
  }, [records, extraStatusDates, incapacityDatesSet, permissionDatesSet]);

  const [expandedChartDays, setExpandedChartDays] = React.useState<Set<string>>(
    () => new Set()
  );

  const chartDisplayOptions = React.useMemo(
    () => ({
      branchId,
      authorizedSchedule,
      payrollSchedules,
      realCheckRecords,
      partnerId,
      periodStart,
      periodEnd,
      payrollBranchMeta,
      fullDayHours,
    }),
    [
      branchId,
      authorizedSchedule,
      payrollSchedules,
      realCheckRecords,
      partnerId,
      periodStart,
      periodEnd,
      payrollBranchMeta,
      fullDayHours,
    ]
  );

  const visitsByFecha = React.useMemo(
    () =>
      buildPartnerVisitHoursByFecha(
        realCheckRecords || [],
        partnerId,
        periodStart,
        periodEnd,
        payrollBranchMeta
      ),
    [realCheckRecords, partnerId, periodStart, periodEnd, payrollBranchMeta]
  );

  const displayByFecha = React.useMemo(
    () => buildChartDetailedDisplayByFecha(tableRows, chartDisplayOptions),
    [tableRows, chartDisplayOptions]
  );

  const toggleChartDayExpanded = (fecha: string) => {
    React.startTransition(() => {
      setExpandedChartDays((prev) => {
        const next = new Set(prev);
        if (next.has(fecha)) {
          next.delete(fecha);
        } else {
          next.add(fecha);
        }
        return next;
      });
    });
  };

  const renderFechaCell = (
    fecha: string,
    hasPendingLeader: boolean,
    extra?: React.ReactNode,
    expandOptions?: {
      hasMultipleVisits: boolean;
      isExpanded: boolean;
      onToggle: () => void;
    }
  ) => (
    <td
      style={{
        ...cdwCellStyle,
        textAlign: "center",
        minWidth: 0,
        cursor: expandOptions?.hasMultipleVisits ? "pointer" : undefined,
      }}
      onClick={
        expandOptions?.hasMultipleVisits
          ? expandOptions.onToggle
          : undefined
      }
      title={
        expandOptions?.hasMultipleVisits
          ? expandOptions.isExpanded
            ? "Ocultar checadas del día"
            : "Ver checadas del día"
          : undefined
      }
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.2rem",
          minWidth: 0,
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.25rem",
            minWidth: 0,
          }}
        >
          {expandOptions?.hasMultipleVisits && (
            <i
              className={`fas fa-chevron-${
                expandOptions.isExpanded ? "down" : "right"
              }`}
              style={{ fontSize: "0.6rem", color: "#64748b" }}
            />
          )}
          <span style={{ fontWeight: 600, color: "#334155" }}>
            {formatPayrollDisplayDate(fecha)}
          </span>
          {hasPendingLeader && <PendingLeaderDotChart />}
          {extra}
        </div>
      </div>
    </td>
  );

  return (
    <div
      className="chart-days-table-wrap"
      style={{ ...cdwTableWrapStyle, marginBottom: 0 }}
    >
      <style>{`
        .chart-days-table-wrap {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .chart-days-table-wrap::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        .chart-days-worked-table thead tr th:nth-child(1),
        .chart-days-worked-table tbody td:nth-child(1),
        .chart-days-worked-table thead tr th:nth-child(8),
        .chart-days-worked-table tbody td:nth-child(8) {
          min-width: 0;
          overflow: hidden;
          box-sizing: border-box;
        }
        .chart-days-worked-table thead tr th:nth-child(4),
        .chart-days-worked-table tbody td:nth-child(4) {
          min-width: 4.5rem;
          overflow: hidden;
          box-sizing: border-box;
          padding-left: 0.2rem;
          padding-right: 0.2rem;
        }
        .chart-days-worked-table thead tr th:nth-child(5),
        .chart-days-worked-table tbody td:nth-child(5) {
          min-width: 5.5rem;
          overflow: hidden;
          box-sizing: border-box;
          padding-left: 0.2rem;
          padding-right: 0.2rem;
        }
        .chart-days-worked-table > :not(caption) > * > * {
          border-left: none !important;
          border-right: none !important;
        }
        .chart-days-worked-table thead tr:first-child th {
          border-top: none !important;
          background-color: #ecfeff !important;
        }
        .chart-days-worked-table tbody td {
          border-top: none !important;
        }
        .chart-days-worked-table tbody tr.payroll-day-expanded > td {
          border-bottom: none !important;
        }
        .chart-days-worked-table tbody tr.payroll-day-visit-row > td,
        .chart-days-worked-table tbody tr.payroll-day-visit-total > td {
          border-top: none !important;
          border-bottom: none !important;
        }
        .chart-days-worked-table tbody tr:last-child td {
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .chart-days-worked-table tbody tr.payroll-day-visit-total:last-child td,
        .chart-days-worked-table tbody tr.payroll-day-visit-row:last-child td {
          border-bottom: none !important;
        }
      `}</style>
      <table
        className="table table-hover mb-0 chart-days-worked-table"
        style={cdwTableStyle}
      >
        <colgroup>
          <col style={{ width: "12%" }} />
          <col style={{ width: "10.5%" }} />
          <col style={{ width: "10.5%" }} />
          <col style={{ width: "10.5%" }} />
          <col style={{ width: "11.5%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            boxShadow: "0 2px 0 #99f6e4",
          }}
        >
          <tr>
            <th style={cdwHeaderThStyle}>Fecha</th>
            <th style={cdwHeaderThStyle}>Entrada programada</th>
            <th style={cdwHeaderThStyle}>Salida programada</th>
            <th style={cdwHeaderThStyle}>Entrada registrada</th>
            <th style={cdwHeaderThStyle}>Salida registrada</th>
            <th style={cdwHeaderThStyle}>Horas dentro del horario</th>
            <th style={cdwHeaderThStyle}>Horas registradas</th>
            <th style={cdwHeaderThStyle}>Horas extras</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((record, index) => {
            const isFreeDay = freeDaysDates?.has(record.fecha) || false;
            const isCelebrityDay =
              celebrityDaysDates?.has(record.fecha) || false;
            const isIncapacityDay = incapacityDatesSet.has(record.fecha);
            const isPermissionDay = permissionDatesSet.has(record.fecha);
            const isFreeDayEnjoyed = record.isFreeDayEnjoyed || false;
            const hasPendingLeaderIncident =
              pendingLeaderReviewDates?.has(record.fecha) ?? false;
            const isNoProgramadoPending =
              pendingNoProgramadoDates?.has(record.fecha) ?? false;

            if (record.isIncapacityOnly || record.isPermissionOnly) {
              return (
                <tr
                  key={`status-row-${record.fecha}`}
                  className="text-center table-warning"
                >
                  {renderFechaCell(
                    record.fecha,
                    hasPendingLeaderIncident,
                    <>
                      {record.isPermissionOnly && (
                        <span className="badge bg-primary">Permiso</span>
                      )}
                      {record.isIncapacityOnly && (
                        <span className="badge bg-danger">Incapacidad</span>
                      )}
                    </>
                  )}
                  <td
                    colSpan={7}
                    style={{
                      ...cdwCellStyle,
                      ...cdwCellWrapStyle,
                      fontWeight: 600,
                    }}
                  >
                    Día con{" "}
                    {record.isPermissionOnly && record.isIncapacityOnly
                      ? "permiso e incapacidad registrados"
                      : record.isPermissionOnly
                      ? "permiso registrado"
                      : "incapacidad registrada"}
                  </td>
                </tr>
              );
            }

            if (isFreeDayEnjoyed) {
              return (
                <tr
                  key={`free-day-${index}`}
                  className="table-info text-center"
                >
                  {renderFechaCell(record.fecha, hasPendingLeaderIncident)}
                  <td
                    colSpan={7}
                    style={{
                      ...cdwCellStyle,
                      ...cdwCellWrapStyle,
                      fontWeight: 700,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    Día de descanso disfrutado
                  </td>
                </tr>
              );
            }

            if (record.isAbsence) {
              const absenceDisplay =
                displayByFecha.get(record.fecha) ??
                record;
              return (
                <tr
                  key={`absence-${index}`}
                  className="text-center table-danger"
                >
                  {renderFechaCell(
                    record.fecha,
                    hasPendingLeaderIncident,
                    <span className="badge bg-danger">Falta</span>
                  )}
                  <td style={cdwCellProgramadoStyle}>
                    {absenceDisplay.entradaProgramada}
                  </td>
                  <td style={cdwCellProgramadoStyle}>
                    {absenceDisplay.salidaProgramada}
                  </td>
                  <td
                    colSpan={5}
                    style={{
                      ...cdwCellStyle,
                      ...cdwCellWrapStyle,
                      fontWeight: 600,
                    }}
                  >
                    No tuvo chequeo registrado
                  </td>
                </tr>
              );
            }

            const displayRecord =
              displayByFecha.get(record.fecha) ?? record;
            const horasRegistradasDisplay =
              displayRecord.horasRegistradas ?? record.horasRegistradas;
            const horasEnHorarioDisplay =
              displayRecord.horasTrabajadasEnHorario ??
              record.horasTrabajadasEnHorario;
            const horasExtrasDisplay =
              displayRecord.horasExtras ?? record.horasExtras;

            const chartDateKey = moment(record.fecha, "DD-MM-YYYY", true).isValid()
              ? moment(record.fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
              : record.fecha;

            const rawDayVisits =
              Array.isArray(record.visits) && record.visits.length > 0
                ? enrichVisitsWithBranchNames(
                    record.visits.map((v: any) =>
                      mapStoredVisit(v, chartDateKey)
                    ),
                    realCheckRecords || []
                  )
                : visitsByFecha.get(record.fecha) || [];
            const chartDayVisits = dedupePayrollVisitsByPair(rawDayVisits);

            const programadoSlots = getProgrammedSlotsForFecha(
              record.fecha,
              authorizedSchedule,
              payrollSchedules
            );
            const daySchedulesForFecha = dayScheduleSourcesForFecha(
              record.fecha,
              authorizedSchedule,
              payrollSchedules
            );
            const partnerNightForProgramado =
              resolvePayrollPartnerNightScheduleEnabled({
                partnerId,
                branchId,
                realCheckRecords,
                payrollBranchMeta,
              });
            const programadoResolved = resolveProgramadoDisplayForPayrollDay(
              daySchedulesForFecha,
              chartDayVisits,
              { nightScheduleEnabled: partnerNightForProgramado }
            );
            // Si hay horario real en agenda → usarlo. Solo sin programar se copia la checada.
            const hasRealSchedule = programadoSlots.length > 0;
            const showVariosProgramado = hasRealSchedule && programadoSlots.length > 1;
            const programadoEntrada = hasRealSchedule
              ? showVariosProgramado
                ? "Varios"
                : programadoSlots[0].entrada
              : displayRecord.entradaRegistrada &&
                displayRecord.entradaRegistrada !== "N/A"
                ? displayRecord.entradaRegistrada
                : programadoResolved.entrada !== "N/A"
                  ? programadoResolved.entrada
                  : displayRecord.entradaProgramada;
            const programadoSalida = hasRealSchedule
              ? showVariosProgramado
                ? "Varios"
                : programadoSlots[0].salida
              : displayRecord.salidaRegistrada &&
                displayRecord.salidaRegistrada !== "N/A"
                ? displayRecord.salidaRegistrada
                : programadoResolved.salida !== "N/A"
                  ? programadoResolved.salida
                  : displayRecord.salidaProgramada;
            const hasMultipleChecadas = shouldShowVariosRegistros(chartDayVisits);
            const hasMultipleProgramado = programadoSlots.length > 1;
            const canExpandDay = hasMultipleChecadas || hasMultipleProgramado;
            const isChartDayExpanded = expandedChartDays.has(record.fecha);

            return (
              <React.Fragment key={`chart-day-${index}`}>
              <tr
                className={`text-center${
                  isChartDayExpanded && canExpandDay
                    ? " payroll-day-expanded"
                    : ""
                }`}
              >
                {renderFechaCell(
                  record.fecha,
                  hasPendingLeaderIncident,
                  <>
                    {hasMultipleProgramado && (
                      <span
                        className="badge"
                        style={{
                          background: "#d1fae5",
                          color: "#065f46",
                          fontSize: "0.65rem",
                        }}
                      >
                        Varios horarios
                      </span>
                    )}
                    {isFreeDay && !isFreeDayEnjoyed && (
                      <span className="badge bg-info">Día libre</span>
                    )}
                    {isCelebrityDay && (
                      <span className="badge bg-warning">Día festivo</span>
                    )}
                    {isPermissionDay && (
                      <span className="badge bg-primary">Permiso</span>
                    )}
                    {isIncapacityDay && (
                      <span className="badge bg-danger">Incapacidad</span>
                    )}
                  </>,
                  canExpandDay
                    ? {
                        hasMultipleVisits: true,
                        isExpanded: isChartDayExpanded,
                        onToggle: () => toggleChartDayExpanded(record.fecha),
                      }
                    : undefined
                )}
                <td style={cdwCellProgramadoStyle}>
                  {programadoEntrada}
                </td>
                <td style={cdwCellProgramadoStyle}>
                  {programadoSalida}
                </td>
                <td style={cdwCellRealStyle}>
                  <ChartRealTimeCell time={displayRecord.entradaRegistrada} />
                </td>
                <td style={cdwCellRealStyle}>
                  <ChartRealTimeCell
                    time={displayRecord.salidaRegistrada}
                    crossDayDate={displayRecord.salidaRegistradaFecha}
                  />
                </td>
                <td style={cdwCellStyle}>
                  {formatPayrollHoursDecimal(horasEnHorarioDisplay)}
                </td>
                <td style={cdwCellStyle}>
                  {formatPayrollHoursDecimal(horasRegistradasDisplay)}
                </td>
                <td style={{ ...cdwCellStyle, ...cdwCellWrapStyle }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "0.25rem",
                      minWidth: 0,
                    }}
                  >
                    <span>{formatExtraHours(horasExtrasDisplay)}</span>
                    {(hasPendingLeaderIncident ||
                      isNoProgramadoPending ||
                      (!datesWithIncidents.has(record.fecha) &&
                        horasExtrasDisplay > 0)) && (
                      <>
                        {isNoProgramadoPending ? (
                          <span className="badge bg-warning text-dark">
                            No programado
                          </span>
                        ) : hasPendingLeaderIncident ? (
                          <span className="badge bg-warning text-dark">
                            Pendiente líder
                          </span>
                        ) : horasExtrasDisplay > 0 ? (
                          <span className="badge bg-secondary">
                            Sin incidencia
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                </td>
              </tr>
              {isChartDayExpanded && hasMultipleProgramado ? (
                <ChartDayProgramadoExpandRows
                  slots={programadoSlots}
                  fecha={record.fecha}
                />
              ) : null}
              {isChartDayExpanded && hasMultipleChecadas ? (
                <PayrollDayVisitRows
                  visits={chartDayVisits}
                  dateKey={chartDateKey}
                  variant="chart"
                />
              ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="text-center">
            <td
              colSpan={5}
              style={{
                ...cdwFooterCellStyle,
                textAlign: "right",
              }}
            >
              <strong>Totales</strong>
            </td>
            <td style={cdwFooterCellStyle}>
              {formatPayrollHoursDecimal(
                tableRows.reduce((sum, record) => {
                  const display = displayByFecha.get(record.fecha) ?? record;
                  return (
                    sum +
                    Number(
                      display.horasTrabajadasEnHorario ??
                        record.horasTrabajadasEnHorario ??
                        0
                    )
                  );
                }, 0)
              )}
            </td>
            <td style={cdwFooterCellStyle}>
              {formatPayrollHoursDecimal(
                tableRows.reduce((sum, record) => {
                  const display = displayByFecha.get(record.fecha) ?? record;
                  return (
                    sum +
                    Number(
                      display.horasRegistradas ?? record.horasRegistradas ?? 0
                    )
                  );
                }, 0)
              )}
            </td>
            <td style={{ ...cdwFooterCellStyle, ...cdwCellWrapStyle }}>
              {formatExtraHours(
                tableRows.reduce((sum, record) => {
                  const display = displayByFecha.get(record.fecha) ?? record;
                  return (
                    sum + Number(display.horasExtras ?? record.horasExtras ?? 0)
                  );
                }, 0)
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default ChartDaysWorked;
